import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { io, Socket } from "socket.io-client";
import {
  ChatContextType,
  ChatSession,
  ChatMessage,
  ChatUser,
  ChatUserStatus,
} from "../types/chat";
import api from "../infra/api";
import popSound from "../assets/pop.mp3";

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<ChatUser | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSession, setActiveSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<ChatUserStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Refs para manter referências atualizadas nos event handlers
  const activeSessionRef = useRef<ChatSession | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const sessionsRef = useRef<ChatSession[]>([]);

  // Atualizar refs quando os estados mudarem
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  // Função para reproduzir som de notificação
  const playNotificationSound = useCallback(() => {
    try {
      // Verificar se o documento está visível (aba ativa)
      if (document.hidden) {
        console.log("Documento não visível, não reproduzindo som");
        return;
      }

      const audio = new Audio(popSound);
      audio.volume = 0.5; // Volume moderado
      audio.play().catch((error) => {
        console.log("Erro ao reproduzir som de notificação:", error);
      });
    } catch (error) {
      console.log("Erro ao criar áudio:", error);
    }
  }, []);

  // Inicializar usuário atual
  useEffect(() => {
    const userId = localStorage.getItem("rh-userId");
    const userName = localStorage.getItem("rh-userName");
    const userEmail = localStorage.getItem("rh-email");

    if (userId) {
      const userData: ChatUser = {
        id: parseInt(userId),
        codigo: userId,
        nome: userName || "",
        nomecomp: userName || "",
        email: userEmail || "",
        ativo: 1,
      };
      setCurrentUser(userData);
    }
  }, []);

  // Inicializar WebSocket
  useEffect(() => {
    const token = localStorage.getItem("rh-token");
    if (!token) return;

    const socketUrl =
      process.env.REACT_APP_SOCKET_URL || "http://localhost:8881";
    console.log("Conectando ao WebSocket em:", socketUrl);
    console.log("Token:", token ? "presente" : "ausente");

    const newSocket = io(socketUrl, {
      auth: {
        token: token,
      },
    });

    newSocket.on("connect", () => {
      console.log("Conectado ao WebSocket");

      // Registrar usuário
      const userId = localStorage.getItem("rh-userId");
      const userName = localStorage.getItem("rh-userName");

      console.log("Registrando usuário no WebSocket:", { userId, userName });

      if (userId) {
        newSocket.emit("newUser", userId, userName, userId);
        console.log("Evento newUser enviado com:", {
          userId,
          userName,
          uuid: userId,
        });
      } else {
        console.error("UserId não encontrado no localStorage");
      }
    });

    // Outros eventos do chat (exceto new_chat_message que é tratado separadamente)

    newSocket.on("message_read", ({ messageId, userId }) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                reads: [
                  ...(message.reads || []),
                  {
                    id: Date.now(), // ID temporário
                    message_id: messageId,
                    user_id: userId,
                    read_at: new Date().toISOString(),
                  },
                ],
              }
            : message
        )
      );
    });

    newSocket.on("user_typing", ({ userId, sessionId }) => {
      // Implementar indicador de digitação
      console.log(`Usuário ${userId} está digitando na sessão ${sessionId}`);
    });

    newSocket.on("user_stopped_typing", ({ userId, sessionId }) => {
      // Remover indicador de digitação
      console.log(`Usuário ${userId} parou de digitar na sessão ${sessionId}`);
    });

    newSocket.on("chat_error", (errorData) => {
      setError(errorData.message);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Configurar eventos do WebSocket quando socket estiver pronto
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (message: ChatMessage) => {
      console.log("Nova mensagem recebida via WebSocket:", message);
      console.log("Sessão ativa atual:", activeSessionRef.current?.id);
      console.log("Mensagem da sessão:", message.session_id);

      // Verificar se a mensagem é da sessão ativa usando ref
      if (
        activeSessionRef.current &&
        message.session_id === activeSessionRef.current.id
      ) {
        console.log("Adicionando mensagem às mensagens da sessão ativa");
        setMessages((prev) => {
          // Verificar se já existe uma mensagem com o mesmo conteúdo e horário próximo (mensagem temporária)
          const tempMessageIndex = prev.findIndex(
            (msg) =>
              msg.sender_id === message.sender_id &&
              msg.message_content === message.message_content &&
              Math.abs(
                new Date(msg.sent_at).getTime() -
                  new Date(message.sent_at).getTime()
              ) < 5000 // 5 segundos de diferença
          );

          if (tempMessageIndex !== -1) {
            console.log("Substituindo mensagem temporária pela mensagem real");
            const newMessages = [...prev];
            newMessages[tempMessageIndex] = message;
            return newMessages;
          }

          // Verificar se a mensagem já existe (por ID real)
          const messageExists = prev.some((msg) => msg.id === message.id);
          if (messageExists) {
            console.log("Mensagem já existe, não adicionando duplicata");
            return prev;
          }

          console.log("Mensagens anteriores:", prev.length);
          const newMessages = [...prev, message];
          console.log("Novas mensagens:", newMessages.length);
          return newMessages;
        });
      } else {
        console.log("Mensagem não adicionada - sessão não ativa ou diferente");
      }

      // Atualizar última mensagem na sessão
      setSessions((prev) =>
        prev.map((session) =>
          session.id === message.session_id
            ? { ...session, last_message_at: message.sent_at }
            : session
        )
      );

      // Reproduzir som de notificação se a mensagem não for do próprio usuário
      const currentUserId = parseInt(localStorage.getItem("rh-userId") || "0");
      if (message.sender_id !== currentUserId) {
        console.log(
          "Reproduzindo som de notificação para mensagem de outro usuário"
        );
        playNotificationSound();
      } else {
        console.log("Mensagem do próprio usuário, não reproduzindo som");
      }
    };

    // Registrar event handlers
    socket.on("new_chat_message", handleNewMessage);

    return () => {
      // Limpar event handlers
      socket.off("new_chat_message", handleNewMessage);
    };
  }, [socket]);

  // Carregar sessões do usuário
  const loadUserSessions = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL + "api/chat/sessions";
      const response = await api(apiUrl);

      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      } else {
        setError("Erro ao carregar sessões");
      }
    } catch (err) {
      setError("Erro de conexão");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Carregar mensagens de uma sessão
  const loadMessages = useCallback(async (sessionId: number, page = 1) => {
    setLoading(true);
    try {
      const apiUrl =
        process.env.REACT_APP_API_URL +
        `api/chat/sessions/${sessionId}/messages?page=${page}&limit=50`;
      const response = await api(apiUrl);

      if (response.ok) {
        const data = await response.json();
        if (page === 1) {
          setMessages(data);
        } else {
          setMessages((prev) => [...data, ...prev]);
        }
      } else {
        setError("Erro ao carregar mensagens");
      }
    } catch (err) {
      setError("Erro de conexão");
    } finally {
      setLoading(false);
    }
  }, []);

  // Enviar mensagem
  const sendMessage = useCallback(
    async (
      content: string,
      type = "text" as ChatMessage["message_type"],
      file?: File
    ) => {
      if (!activeSession || !currentUser) return;

      // Adicionar mensagem temporária imediatamente (optimistic update)
      const tempMessage: ChatMessage = {
        id: Date.now(), // ID temporário
        session_id: activeSession.id,
        sender_id: currentUser.id,
        sender_name: currentUser.nome || currentUser.nomecomp,
        message_content: content,
        message_type: type,
        sent_at: new Date().toISOString(),
        file_url: undefined,
        file_name: undefined,
        file_size: undefined,
        is_edited: 0,
        is_deleted: 0,
        reads: [],
      };

      setMessages((prev) => {
        console.log("Adicionando mensagem temporária:", tempMessage);
        return [...prev, tempMessage];
      });

      // Se for sessão externa (WhatsApp), enviar via API do WhatsApp
      if (activeSession.session_type === "external") {
        try {
          const apiUrl = process.env.REACT_APP_API_URL + "api/whatsapp/send";
          const response = await api(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              session_id: activeSession.id,
              message: content,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error("Erro ao enviar mensagem WhatsApp:", errorData);
            // Remover mensagem temporária em caso de erro
            setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
            throw new Error(errorData.error || "Erro ao enviar mensagem");
          }

          const data = await response.json();
          console.log("Mensagem WhatsApp enviada:", data);
        } catch (error) {
          console.error("Erro ao enviar mensagem externa:", error);
          throw error;
        }
        return;
      }

      // Para sessões internas, usar WebSocket
      if (!socket) return;

      const messageData = {
        session_id: activeSession.id,
        message_content: content,
        message_type: type,
        file_url: null,
        file_name: null,
        file_size: null,
      };

      // Se houver arquivo, fazer upload primeiro
      if (file) {
        // Implementar upload de arquivo
        // messageData.file_url = uploadedFileUrl;
        // messageData.file_name = file.name;
        // messageData.file_size = file.size;
      }

      console.log("Enviando mensagem via WebSocket:", messageData);
      console.log("Socket conectado:", socket.connected);
      console.log("Usuario atual:", currentUser);

      socket.emit("send_chat_message", messageData);
    },
    [activeSession, socket, currentUser]
  );

  // Criar nova sessão
  const createSession = useCallback(
    async (
      participants: number[],
      sessionName?: string,
      sessionType = "private" as "private" | "group"
    ) => {
      if (!currentUser) {
        console.error("CurrentUser não encontrado:", currentUser);
        console.error("LocalStorage user:", localStorage.getItem("user"));
        throw new Error("Usuário não logado");
      }

      console.log("Criando sessão com usuário:", currentUser);
      setLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL + "api/chat/sessions";
        const response = await api(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            session_name: sessionName,
            session_type: sessionType,
            participant_ids: participants,
          }),
        });

        if (response.ok) {
          const newSession = await response.json();
          setSessions((prev) => [newSession, ...prev]);
          return newSession;
        } else {
          throw new Error("Erro ao criar sessão");
        }
      } catch (err) {
        setError("Erro ao criar sessão");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [currentUser]
  );

  // Marcar mensagem como lida
  const markAsRead = useCallback(
    async (messageId: number) => {
      if (!socket) return;

      socket.emit("mark_message_read", messageId);
    },
    [socket]
  );

  // Atualizar status de digitação
  const updateTypingStatus = useCallback(
    (sessionId: number, isTyping: boolean) => {
      if (!socket) return;

      if (isTyping) {
        socket.emit("typing_start", { sessionId });
      } else {
        socket.emit("typing_stop", { sessionId });
      }
    },
    [socket]
  );

  // Buscar sessões
  const searchSessions = useCallback(
    (query: string) => {
      return sessions.filter(
        (session) =>
          session.session_name?.toLowerCase().includes(query.toLowerCase()) ||
          session.participants?.some(
            (p) =>
              p.user?.nome?.toLowerCase().includes(query.toLowerCase()) ||
              p.user?.nomecomp?.toLowerCase().includes(query.toLowerCase())
          )
      );
    },
    [sessions]
  );

  // Buscar mensagens
  const searchMessages = useCallback(
    (query: string, sessionId?: number) => {
      let messagesToSearch = messages;
      if (sessionId) {
        messagesToSearch = messages.filter((m) => m.session_id === sessionId);
      }

      return messagesToSearch.filter((message) =>
        message.message_content.toLowerCase().includes(query.toLowerCase())
      );
    },
    [messages]
  );

  // Entrar em uma sessão
  const handleSetActiveSession = useCallback(
    (session: ChatSession | null) => {
      // Sair da sessão anterior
      if (activeSession && socket) {
        socket.emit("leave_chat_session", activeSession.id);
      }

      // Entrar na nova sessão
      if (session && socket) {
        console.log("Entrando na sessão de chat:", session.id);
        console.log("Socket conectado:", socket.connected);
        socket.emit("join_chat_session", session.id);
      } else {
        console.log("Não foi possível entrar na sessão:", {
          session: !!session,
          socket: !!socket,
        });
      }

      setActiveSession(session);
    },
    [activeSession, socket]
  );

  // Carregar sessões quando o usuário for definido
  useEffect(() => {
    if (currentUser) {
      loadUserSessions();
    }
  }, [currentUser, loadUserSessions]);

  // Recarregar mensagens quando a sessão ativa muda
  useEffect(() => {
    if (activeSession) {
      console.log(
        "Sessão ativa mudou, recarregando mensagens:",
        activeSession.id
      );
      loadMessages(activeSession.id);
    } else {
      // Limpar mensagens quando não há sessão ativa
      setMessages([]);
    }
  }, [activeSession, loadMessages]);

  const value: ChatContextType = {
    currentUser,
    sessions,
    activeSession,
    messages,
    onlineUsers,
    loading,
    error,
    setActiveSession: handleSetActiveSession,
    sendMessage,
    createSession,
    loadMessages,
    markAsRead,
    updateTypingStatus,
    searchSessions,
    searchMessages,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
